import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtTime } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/pages/employee/WeeklySummary";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, AlertTriangle, Eye, Search, Download, X } from "lucide-react";

/**
 * Daily Punches — admin troubleshooting view.
 *
 * Lets the admin pick a date and see EVERY active employee's clock punches
 * for that day, even when no submission was finalized. Clicking a row opens
 * the day-detail view (same UI as a finalized submission), where punches and
 * jobs can be edited in place.
 */
export default function AdminDailyPunches() {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/day-activity`, { params: { date: d } });
      setData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const shiftDate = (days) => {
    const dt = new Date(date + "T00:00:00");
    dt.setDate(dt.getDate() + days);
    setDate(dt.toISOString().slice(0, 10));
  };

  const niceLabel = new Date(date + "T00:00:00").toLocaleDateString([], {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });

  const rows = data?.rows || [];

  // Unique employee names for the autocomplete datalist — matches the
  // Paystubs / Submissions filter pattern.
  const employeeSuggestions = useMemo(() => {
    return Array.from(new Set(rows.map((r) => r.employee_name).filter(Boolean))).sort();
  }, [rows]);

  // Client-side filter — case-insensitive name/email contains. Date range
  // doesn't apply here since this view is inherently single-day (change the
  // date via the navigator to look at other days).
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.employee_name || "").toLowerCase();
      const email = (r.employee_email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [rows, search]);

  const hasFilters = !!search;
  const clearFilters = () => setSearch("");

  const exportCsv = () => {
    if (filteredRows.length === 0) { toast.info("Nothing to export — adjust your filters"); return; }
    const csvRows = [
      ["Date", "Employee", "Email", "Status", "Clock In", "Lunch Start", "Lunch End", "Clock Out", "Punches", "Net Hours", "Jobs"],
      ...filteredRows.map((r) => [
        date,
        r.employee_name || "",
        r.employee_email || "",
        (r.status || "").replace(/_/g, " "),
        r.clock_in || "",
        r.lunch_start || "",
        r.lunch_end || "",
        r.clock_out || "",
        r.punch_count ?? 0,
        Number(r.net_hours || 0).toFixed(2),
        r.job_count ?? 0,
      ]),
    ];
    const csv = csvRows.map((r) => r.map((cell) => {
      const val = String(cell ?? "");
      return /[",\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const emp = search ? `-${search.replace(/[^a-z0-9]+/gi, "_")}` : "";
    a.href = url;
    a.download = `daily-punches-${date}${emp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-daily-punches-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
        <div className="mt-1 flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900">Daily Punches</h1>
          <p className="text-xs text-slate-500 tabular-nums" data-testid="daily-punches-count-note">
            Showing <b className="text-slate-900">{filteredRows.length}</b> of {rows.length} employees
            {hasFilters && <> · <button type="button" onClick={clearFilters} className="text-sky-600 hover:underline" data-testid="daily-punches-clear-filters-btn">clear filters</button></>}
          </p>
        </div>
        <p className="text-sm text-slate-600 mt-1">See every employee&apos;s clock activity for any day — even days nobody finalized. Click a row to inspect or edit the punches.</p>
      </header>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => shiftDate(-1)} data-testid="prev-day-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-44"
              data-testid="date-picker"
            />
            <Button variant="outline" size="sm" onClick={() => shiftDate(1)} data-testid="next-day-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDate(todayIso())} data-testid="today-btn">Today</Button>
            <Button variant="ghost" size="sm" onClick={() => load(date)} disabled={loading} data-testid="reload-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <span className="text-sm font-semibold text-slate-700 ml-2">{niceLabel}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200" data-testid="daily-punches-filter-panel">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Employee</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="search"
                  autoComplete="off"
                  list="daily-punches-employee-list"
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="daily-punches-employee-search"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md hover:bg-slate-100 flex items-center justify-center"
                    data-testid="daily-punches-employee-search-clear"
                  >
                    <X className="h-3.5 w-3.5 text-slate-500" />
                  </button>
                )}
                <datalist id="daily-punches-employee-list">
                  {employeeSuggestions.map((n) => <option key={n} value={n} />)}
                </datalist>
              </div>
            </div>
            <Button
              onClick={exportCsv}
              disabled={filteredRows.length === 0}
              variant="outline"
              className="h-10 shrink-0"
              data-testid="daily-punches-export-csv-btn"
              title={filteredRows.length === 0 ? "Nothing matches your filters" : "Download filtered set as CSV"}
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0">
          {!data && <div className="p-10 text-center text-slate-500">Loading…</div>}
          {data && rows.length === 0 && (
            <div className="p-10 text-center text-slate-500" data-testid="no-rows-empty">
              No active employees found for this company.
            </div>
          )}
          {data && rows.length > 0 && filteredRows.length === 0 && (
            <div className="p-10 text-center text-slate-500" data-testid="daily-punches-empty-state">
              No employees match your filters. Try a different search term.
            </div>
          )}
          {data && filteredRows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="daily-punches-table">
                <thead className="text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Clock In</th>
                    <th className="text-left px-4 py-2">Lunch</th>
                    <th className="text-left px-4 py-2">Clock Out</th>
                    <th className="text-right px-4 py-2">Punches</th>
                    <th className="text-right px-4 py-2">Net Hrs</th>
                    <th className="text-right px-4 py-2">Jobs</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRows.map((r) => {
                    const onClick = () => {
                      // If a submission exists, open it directly so the admin
                      // can approve/reject. Otherwise open the day-detail view.
                      if (r.submission_id) navigate(`/admin/submissions/${r.submission_id}`);
                      else navigate(`/admin/submissions/day?user=${r.user_id}&date=${date}`);
                    };
                    return (
                      <tr
                        key={r.user_id}
                        className="hover:bg-sky-50/50 cursor-pointer"
                        onClick={onClick}
                        data-testid={`daily-row-${r.user_id}`}
                      >
                        <td className="px-4 py-2.5">
                          <p className="font-semibold text-slate-900">{r.employee_name}</p>
                          <p className="text-xs text-slate-500">{r.employee_email}</p>
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusBadge status={r.status} />
                          {r.has_flagged_punches && (
                            <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800" title="Flagged punch — exceeded safety cap">
                              <AlertTriangle className="h-3 w-3" /> flagged
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-slate-700">{fmtTime(r.clock_in) || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-700">
                          {r.lunch_start || r.lunch_end ? (
                            <span>{fmtTime(r.lunch_start) || "—"} → {fmtTime(r.lunch_end) || "—"}</span>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-slate-700">{fmtTime(r.clock_out) || "—"}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-slate-700">{r.punch_count}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{r.net_hours.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{r.job_count}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-sky-700" data-testid={`view-day-${r.user_id}`}>
                            <Eye className="h-3.5 w-3.5 mr-1" /> View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function todayIso() {
  // Use local date — backend uses company tz on its side; close enough for the
  // initial date pick. Admin can navigate with arrows for exact day.
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
